/**
 * VLE Consent Types
 *
 * TypeScript interfaces for VLE Dashboard components.
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

// ============================================
// API Request/Response Types
// ============================================

/**
 * Parameters for VLE consent list API
 */
export interface VleConsentListParams {
  pageno?: number;
  limit?: number;
  from?: string;  // ISO date string for filter
  to?: string;    // ISO date string for filter
  mobile_no?: string;  // Mobile number search
  status?: string;  // Consent status filter
}

/**
 * Single consent record from API
 */
export interface VleConsentRecord {
  id: number;
  consent_id: string | null;
  consent_handle: string | null;  // Used as Request ID in UI (PRD: "Unique ID generated by system")
  status: string;
  identifier_type: string;
  identifier_value: string;  // Full 10-digit mobile (PRD: no masking for mobile)
  masked_pan?: string;  // Partially masked PAN (PRD line 119: "PAN (partially masked)")
  add_info?: {
    pan?: string;
    use_case?: string;
    [key: string]: unknown;
  };
  fi_data_range_from: string | null;
  fi_data_range_to: string | null;
  fip_id: string | null;
  product_id: string | null;
  created_at: string;
  updated_at: string;
  consent_data_fetches?: Array<{
    data_life_status: string;
    fip_id?: string;
    created_at?: string;
    has_transactions?: boolean;
  }>;
  // Payment status fields (from Phase 4)
  payment_status?: string;  // NOT_INITIATED | PENDING | SUCCESS | FAILED
  payment_txn_status?: string | null;  // 100=SUCCESS, 101=FAILED, etc.
  payment_txn_amount?: number | null;
  payment_completed_at?: string | null;
}

/**
 * Response from VLE consent list API
 */
export interface VleConsentListResponse {
  status?: string;
  data: {
    count: number;
    rows: VleConsentRecord[];
    page: number;
    limit: number;
  };
}

/**
 * Payload for creating a new consent request
 */
export interface VleConsentCreatePayload {
  partyIdentifierType: string;
  partyIdentifierValue: string;
  productIDList: string[];
  fipID?: string[];
  accountID: string;
  vua: string;
  accountAggregator: string;
  request_type?: string;
  config?: {
    fiDataRange?: {
      from: string;  // ISO 8601 date string
      to: string;    // ISO 8601 date string
    };
  };
  add_info?: {
    pan?: string;
    use_case?: string;
    [key: string]: unknown;
  };
}

/**
 * Response from consent creation API
 */
export interface VleConsentCreateResponse {
  status: string;
  data?: {
    consent_handle?: string;
    webRedirectionUrl?: string;
    txn_id?: string;
    // Provider discriminator: ONEAPP -> open the URL in an iframe modal; ONEMONEY (or absent) -> redirect.
    provider?: 'ONEAPP' | 'ONEMONEY';
  };
  message?: string;
}

// Response of POST /csc/consent/sync (OneApp data sync fired on the iframe SUCCESS event).
export interface VleConsentSyncResponse {
  status: string;
  data?: {
    status?: string; // 'ACTIVE' | 'NO_DATA'
    has_data?: boolean;
  };
  message?: string;
}

/**
 * OneApp consent-journey event codes delivered on the SDK postMessage `eventCode`. Single source
 * of truth for OneAppConsentModal. Combines the SDK completion event (SUCCESS) with the OneApp
 * consent-status catalog (DATA_READY, AA_CONSENT_*, DATA_*, USER_EXITED, EXPIRED, ...). The modal
 * consumes these permissively: any code not in the success/terminal sets below is an intermediate
 * state and is ignored so the journey continues (and unknown/new codes never break the flow).
 */
export enum OneAppEventCode {
  // success — data staged & ready; pull it via syncConsent (flow ends here)
  SUCCESS = 'SUCCESS',
  DATA_READY = 'DATA_READY',

  // terminal failures — status catalog
  AA_CONSENT_REJECTED = 'AA_CONSENT_REJECTED',
  AA_CONSENT_FAILED = 'AA_CONSENT_FAILED',
  DATA_NOT_PROVIDED = 'DATA_NOT_PROVIDED',
  DATA_DOWNLOAD_FAILED = 'DATA_DOWNLOAD_FAILED',
  DATA_UPLOAD_FAILED = 'DATA_UPLOAD_FAILED',
  DATA_SHARING_REJECTED = 'DATA_SHARING_REJECTED',
  USER_EXITED = 'USER_EXITED',
  EXPIRED = 'EXPIRED',
  // terminal failures — SDK / legacy aliases
  FAILURE = 'FAILURE',
  CONSENT_REJECTED = 'CONSENT_REJECTED',
  AA_CONSENT_DENIED = 'AA_CONSENT_DENIED',
  AA_FAILURE = 'AA_FAILURE',
  TECHNICAL_ERROR = 'TECHNICAL_ERROR',
  USER_REJECTED = 'USER_REJECTED',
  USER_EXIT = 'USER_EXIT',
  USER_PRESS_CLOSE = 'USER_PRESS_CLOSE',
  USER_PRESS_BACK = 'USER_PRESS_BACK',
  SESSION_TIMEOUT = 'SESSION_TIMEOUT',

  // intermediate / in-progress — ignored, journey continues
  PENDING = 'PENDING',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  AA_CONSENT_IN_PROGRESS = 'AA_CONSENT_IN_PROGRESS',
  AA_CONSENT_APPROVED = 'AA_CONSENT_APPROVED',
  DATA_FETCH_IN_PROGRESS = 'DATA_FETCH_IN_PROGRESS',
  DATA_DOWNLOAD_SUCCESS = 'DATA_DOWNLOAD_SUCCESS',
  DATA_SHARING_APPROVED = 'DATA_SHARING_APPROVED',
  AA_NO_ACCOUNT_FOUND = 'AA_NO_ACCOUNT_FOUND',
  AA_DISCOVERY_FAILED = 'AA_DISCOVERY_FAILED',
}

/** Codes that mean "data is ready — call syncConsent". */
export const ONEAPP_SUCCESS_CODES: ReadonlySet<string> = new Set<string>([
  OneAppEventCode.SUCCESS,
  OneAppEventCode.DATA_READY,
]);

/** Codes that end the journey without usable data — close + let the VLE raise a new request. */
export const ONEAPP_TERMINAL_FAILURE_CODES: ReadonlySet<string> = new Set<string>([
  OneAppEventCode.AA_CONSENT_REJECTED,
  OneAppEventCode.AA_CONSENT_FAILED,
  OneAppEventCode.DATA_NOT_PROVIDED,
  OneAppEventCode.DATA_DOWNLOAD_FAILED,
  OneAppEventCode.DATA_UPLOAD_FAILED,
  OneAppEventCode.DATA_SHARING_REJECTED,
  OneAppEventCode.USER_EXITED,
  OneAppEventCode.EXPIRED,
  OneAppEventCode.FAILURE,
  OneAppEventCode.CONSENT_REJECTED,
  OneAppEventCode.AA_CONSENT_DENIED,
  OneAppEventCode.AA_FAILURE,
  OneAppEventCode.TECHNICAL_ERROR,
  OneAppEventCode.USER_REJECTED,
  OneAppEventCode.USER_EXIT,
  OneAppEventCode.USER_PRESS_CLOSE,
  OneAppEventCode.USER_PRESS_BACK,
  OneAppEventCode.SESSION_TIMEOUT,
]);

// ============================================
// Component Props Types
// ============================================

/**
 * Props for VleConsentPage
 */
export interface VleConsentPageProps {
  routeParams?: Record<string, string>;
  queryParams?: Record<string, string>;
}

/**
 * Props for VleConsentTable
 */
export interface VleConsentTableProps {
  consents: VleConsentRecord[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRefresh: () => void;
  onPaymentClick: (consent: VleConsentRecord) => void;
  onShareClick: (consent: VleConsentRecord) => void;
  onRefreshPaymentStatus: (consent: VleConsentRecord) => void;
  refreshingConsentId: string | null;
  onReversePayment: (consent: VleConsentRecord) => void;
  reversingConsentId: string | null;
}

/**
 * Filter state for VleConsentFilters
 */
export interface VleFilterState {
  dateFrom: string;
  dateTo: string;
  mobileSearch: string;
  status: string;
}

/**
 * Props for VleConsentFilters
 */
export interface VleConsentFiltersProps {
  filters: VleFilterState;
  onFilterChange: (filters: VleFilterState) => void;
  onApply: () => void;
  onReset: () => void;
  onCreateClick: () => void;
  isLoading: boolean;
}

/**
 * Form data for CreateStatementDrawer
 * Note: reportTypes is an array to support V3 multi-consent when enabled
 */
export interface CreateStatementFormData {
  mobileNumber: string;
  pan: string;
  reportTypes: string[];  // Array for V3 multi-consent support
  financialInstitute: string;
  periodFrom: string;
  periodTo: string;
}

/**
 * Props for CreateStatementDrawer
 */
export interface CreateStatementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: VleConsentCreateResponse) => void;
  vleUserId: string;
}

/**
 * Product option for dropdown
 */
export interface ProductOption {
  id: string;
  name: string;
  product_id: string;
}

/**
 * FIP (Bank) option for dropdown
 */
export interface FipOption {
  fip_id: string;
  fip_name: string;
  fip_products?: string[];
}

// ============================================
// UI State Types
// ============================================

/**
 * Consent status for display
 */
export type ConsentStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'REJECTED' | 'PAUSED';

/**
 * Data availability status
 */
export type DataStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'FAILED';

/**
 * Payment status (Phase 4)
 */
export type PaymentStatus = 'NOT_REQUIRED' | 'PENDING' | 'PAID' | 'FAILED';

// ============================================
// Constants
// ============================================

export const CONSENT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PENDING: { label: 'PENDING', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  ACTIVE: { label: 'ACTIVE', className: 'bg-green-100 text-green-800 border-green-300' },
  EXPIRED: { label: 'EXPIRED', className: 'bg-gray-100 text-gray-800 border-gray-300' },
  REVOKED: { label: 'REVOKED', className: 'bg-red-100 text-red-800 border-red-300' },
  REJECTED: { label: 'REJECTED', className: 'bg-red-100 text-red-800 border-red-300' },
  PAUSED: { label: 'PAUSED', className: 'bg-orange-100 text-orange-800 border-orange-300' },
};

export const DATA_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  AVAILABLE: { label: 'AVAILABLE', className: 'bg-green-100 text-green-800 border-green-300' },
  NOT_AVAILABLE: { label: 'NOT AVAILABLE', className: 'bg-gray-100 text-gray-800 border-gray-300' },
};

export const PAYMENT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  NOT_INITIATED: { label: 'NOT INITIATED', className: 'bg-gray-100 text-gray-800 border-gray-300' },
  PENDING: { label: 'PENDING', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  SUCCESS: { label: 'SUCCESS', className: 'bg-green-100 text-green-800 border-green-300' },
  FAILED: { label: 'FAILED', className: 'bg-red-100 text-red-800 border-red-300' },
  INSUFFICIENT_BALANCE: { label: 'INSUFFICIENT BALANCE', className: 'bg-red-100 text-red-800 border-red-300' },
  DECLINED: { label: 'DECLINED', className: 'bg-red-100 text-red-800 border-red-300' },
  DROPPED: { label: 'DROPPED', className: 'bg-orange-100 text-orange-800 border-orange-300' },
  UNKNOWN: { label: 'UNKNOWN', className: 'bg-gray-100 text-gray-800 border-gray-300' },
  REVERSED: { label: 'REVERSED', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  REVERSAL_DECLINED: { label: 'REVERSAL DECLINED', className: 'bg-red-100 text-red-800 border-red-300' },
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Payment initiation response
 */
export interface PaymentInitiateResponse {
  status: string;
  data?: {
    form_html: string;
    pay_url: string;
    message: string;
    merchant_txn: string;
    txn_amount: number;
    consent_id: string;
  };
  message?: string;
}

/**
 * Payment config response
 */
export interface PaymentConfigResponse {
  status: string;
  data?: {
    enabled: boolean;
    txn_amount: number;
    mode?: string;
  };
}

/**
 * Payment status check response
 * [SOURCE-TRUTH] FinproCSC/files/06-api-transaction-status.md
 */
export interface PaymentStatusResponse {
  status: string;
  message?: string;
  data?: {
    payment_status: string;
    txn_status: string;
    txn_amount: string;
    csc_txn: string;
    consent_id: string;
    status_description: string;
    too_early?: boolean;
    expired?: boolean;
    csc_error?: boolean;
    csc_error_code?: string;
  };
}

/**
 * Payment reversal response
 * AIDEV-NOTE: CSC Phase 2 — Transaction Reversal (API 2 of 5)
 * [SOURCE-TRUTH] FinproCSC/files/07-api-reversal-refund.md
 */
export interface PaymentReversalResponse {
  status: string;
  message?: string;
  data?: {
    success: boolean;
    consent_id: string;
    reversal_status?: string;
    reversal_reference?: string;
    error_reason?: string;
    message: string;
    csc_error_code?: string;
    seconds_since_payment?: number;
    wait_seconds?: number;
  };
}

/**
 * Share statement response (Phase 5)
 * AIDEV-NOTE: Response from POST /pdfstatement/share endpoint
 */
export interface ShareStatementResponse {
  status: string;
  message?: string;
  data?: {
    sms_sent: boolean;
    remaining_sms_count: number;
  };
  errorCode?: string;
  errorMessage?: string;
}
